import Header from "../../../components/Header";
import { Outlet } from "react-router-dom";
import SideBar from "./SideBar";
import React, { useContext, useState, useEffect } from "react";
import { Context } from "../../../Context";
import { API } from "aws-amplify";
import { toast } from "react-toastify";

// Helper: get today's date in Asia/Kolkata (YYYY-MM-DD)
const getTodayKolkata = () => {
  const tzDate = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const yyyy = tzDate.getFullYear();
  const mm = String(tzDate.getMonth() + 1).padStart(2, "0");
  const dd = String(tzDate.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Helper: convert dateId (e.g. "260929") to "YYYY-MM-DD"
const dateIdToDateStr = (dateId) => {
  if (!dateId) return null;
  const clean = String(dateId).trim();
  if (clean.length === 6) {
    const yy = clean.slice(0, 2);
    const mm = clean.slice(2, 4);
    const dd = clean.slice(4, 6);
    return `20${yy}-${mm}-${dd}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) {
    return clean;
  }
  return null;
};

// Helper: format "YYYY-MM-DD" into friendly "DD MMM YYYY" (e.g. "30 Sep 2026")
const formatDateFriendly = (dateStr) => {
  if (!dateStr || !dateStr.includes("-")) return dateStr || "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * Generate an array of all missing dates between lastClosedStr and todayStr.
 * Uses JavaScript's native Date arithmetic which automatically handles:
 * - Leap years (e.g., Feb 28 -> Feb 29 on leap years, or Feb 28 -> Mar 01 on non-leap years)
 * - 28/29/30/31 day months
 * - Year transitions (Dec 31 -> Jan 01)
 */
const getMissingDatesList = (lastClosedStr, todayStr) => {
  if (!lastClosedStr || !todayStr || !lastClosedStr.includes("-") || !todayStr.includes("-")) {
    return [];
  }
  const [ly, lm, ld] = lastClosedStr.split("-").map(Number);
  const [ty, tm, td] = todayStr.split("-").map(Number);

  const cur = new Date(ly, lm - 1, ld);
  const target = new Date(ty, tm - 1, td);

  const dates = [];
  // Move to day immediately following the last closed day
  cur.setDate(cur.getDate() + 1);

  // Collect all days strictly before today
  while (cur < target) {
    const yyyy = cur.getFullYear();
    const mm = String(cur.getMonth() + 1).padStart(2, "0");
    const dd = String(cur.getDate()).padStart(2, "0");
    dates.push(`${yyyy}-${mm}-${dd}`);
    cur.setDate(cur.getDate() + 1);
  }

  return dates;
};

const Dashboard = () => {
  const { userData, institutionData, setLoader } = useContext(Context);
  const [showDayStartModal, setShowDayStartModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [dateGapInfo, setDateGapInfo] = useState(null);
  const [isTargetDateClosed, setIsTargetDateClosed] = useState(false);
  const [showCustomDateInput, setShowCustomDateInput] = useState(false);

  // Check last closing date and find all missing dates when day is not started
  useEffect(() => {
    const checkDateStatus = async () => {
      if (!institutionData?.institution) return;
      try {
        const todayStr = getTodayKolkata();
        const type = institutionData?.bar_integration === "Yes" ? "bar" : "cafe";

        const response = await API.get(
          "main",
          `/admin/end-day-report/${institutionData.institution}?date=${todayStr}&type=${type}`,
          {}
        );

        // Determine last closed date:
        let lastClosedStr = null;
        if (response?.previousReport?.date) {
          lastClosedStr = response.previousReport.date;
        } else if (response?.previousReport?.dateId) {
          lastClosedStr = dateIdToDateStr(response.previousReport.dateId);
        } else if (institutionData?.dateId) {
          lastClosedStr = dateIdToDateStr(institutionData.dateId);
        }

        if (lastClosedStr) {
          const missingDates = getMissingDatesList(lastClosedStr, todayStr);
          if (missingDates.length > 0) {
            setDateGapInfo({
              lastClosed: lastClosedStr,
              missingDates: missingDates,
              todayDate: todayStr,
            });
            // By default, select the first missing date to maintain continuity in records
            setSelectedDate(missingDates[0]);
          } else {
            setDateGapInfo(null);
            setSelectedDate(todayStr);
          }
        } else {
          setDateGapInfo(null);
          setSelectedDate(todayStr);
        }
      } catch (error) {
        console.error("Error checking date status:", error);
        setSelectedDate(getTodayKolkata());
      }
    };

    if (
      userData?.userType === "admin" &&
      (institutionData?.dayStart === false || institutionData?.dayStart === undefined) &&
      institutionData?.bar_integration === "Yes"
    ) {
      checkDateStatus();
    }
  }, [institutionData?.institution, institutionData?.dayStart, institutionData?.dateId, userData?.userType, institutionData?.bar_integration]);

  // Check if day start modal should be shown
  useEffect(() => {
    if (
      userData?.userType === "admin" &&
      (institutionData?.dayStart === false || institutionData?.dayStart === undefined) &&
      institutionData?.bar_integration === "Yes"
    ) {
      setShowDayStartModal(true);
    } else {
      setShowDayStartModal(false);
    }
  }, [institutionData?.dayStart, userData?.userType, institutionData?.bar_integration]);

  // Check if the currently selected date is already closed
  useEffect(() => {
    const verifySelectedDate = async () => {
      if (!selectedDate || !institutionData?.institution) return;
      try {
        const type = institutionData?.bar_integration === "Yes" ? "bar" : "cafe";
        const response = await API.get(
          "main",
          `/admin/end-day-report/${institutionData.institution}?date=${selectedDate}&type=${type}`,
          {}
        );
        if (response?.success && response?.report && response?.report?.closingBalance !== undefined) {
          setIsTargetDateClosed(true);
        } else {
          setIsTargetDateClosed(false);
        }
      } catch (e) {
        setIsTargetDateClosed(false);
      }
    };
    verifySelectedDate();
  }, [selectedDate, institutionData?.institution, institutionData?.bar_integration]);

  const handleStartDay = async (dateToStart) => {
    const targetDate = dateToStart || selectedDate || getTodayKolkata();
    if (!targetDate) return;

    try {
      console.log("🔄 Starting day process for date:", targetDate);
      setLoader(true);

      const type = institutionData?.bar_integration === "Yes" ? "bar" : "cafe";

      // Verify whether targetDate was already closed
      try {
        const verifyRes = await API.get(
          "main",
          `/admin/end-day-report/${institutionData.institution}?date=${targetDate}&type=${type}`,
          {}
        );
        if (verifyRes?.success && verifyRes?.report && verifyRes?.report?.closingBalance !== undefined) {
          toast.error(`The business day for ${formatDateFriendly(targetDate)} has already been ended/closed.`);
          setLoader(false);
          return;
        }
      } catch (err) {
        // Report lookup failed or not found, proceed
      }

      const apiUrl = `/admin/start-day/${institutionData.institution}?index=${institutionData.index}`;

      const requestBody = {
        dayStart: targetDate, // ISO date string format recognized by backend
        date: targetDate,
        type: type,
      };

      const response = await API.post("main", apiUrl, {
        body: requestBody,
      });

      console.log("✅ API Response:", response);

      if (response.message && response.message.includes("successfully")) {
        toast.success(`Day started successfully for ${formatDateFriendly(targetDate)}!`);
        setShowDayStartModal(false);

        // Clear cached institution data to load fresh dateId & dayStart
        localStorage.removeItem("cafeInstitutionData");

        // Refresh to update context across the app
        window.location.reload();
      } else {
        console.log("❌ Response indicates failure:", response);
        toast.error(response?.message || "Failed to start day. Please try again.");
      }
    } catch (error) {
      console.error("💥 Error starting day:", error);
      toast.error("Failed to start day. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  const handleCancelDayStart = () => {
    setShowDayStartModal(false);
    toast.info("Day start cancelled. Some features may be limited.");
  };

  const todayKolkataStr = getTodayKolkata();

  return (
    <>
      {/* Day Start Modal */}
      {showDayStartModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl relative animate-fadeIn border border-gray-100 max-h-[90vh] flex flex-col my-auto overflow-hidden">
            <div className="p-6 overflow-y-auto text-center">
              <div className="mb-4">
                <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${dateGapInfo ? "bg-amber-100 text-amber-600" : "bg-green-100 text-green-600"}`}>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mt-3 mb-1">
                  Start Business Day
                </h3>
                <p className="text-xs text-gray-500">
                  {institutionData?.companyName || "Bar Inventory & Billing"}
                </p>
              </div>

              {/* If date gap(s) detected */}
              {dateGapInfo && dateGapInfo.missingDates.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 mb-4 text-left">
                  <div className="flex items-start gap-2.5">
                    <div className="p-1 bg-amber-100 rounded text-amber-700 mt-0.5">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wide">
                        Date Gap Detected
                      </h4>
                      <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                        Last closed day was <strong>{formatDateFriendly(dateGapInfo.lastClosed)}</strong>. Choose which date to start operations for:
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Direct Buttons: No scrolling needed! */}
              {dateGapInfo && dateGapInfo.missingDates.length > 0 ? (
                <div className="space-y-3 mb-4">
                  {/* Primary Option: Start for Today */}
                  <button
                    type="button"
                    onClick={() => handleStartDay(dateGapInfo.todayDate || todayKolkataStr)}
                    className="w-full flex items-center justify-between p-3.5 bg-green-600 hover:bg-green-700 text-white rounded-xl shadow-md transition text-left group">
                    <div className="pr-2">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-base">
                          Start for Today ({formatDateFriendly(dateGapInfo.todayDate || todayKolkataStr)})
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-white/20 rounded-full">
                          Today
                        </span>
                      </div>
                      <p className="text-xs text-green-100 mt-0.5">
                        Start day directly for today ({formatDateFriendly(dateGapInfo.todayDate || todayKolkataStr)})
                      </p>
                    </div>
                    <div className="bg-white/20 group-hover:bg-white/30 p-2 rounded-lg ml-2 flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </div>
                  </button>

                  {/* Missing Date Button(s): If 1, show direct button. If more than 1, show compact scrollable list */}
                  {dateGapInfo.missingDates.length === 1 ? (
                    <button
                      type="button"
                      onClick={() => handleStartDay(dateGapInfo.missingDates[0])}
                      className="w-full flex items-center justify-between p-3.5 bg-amber-50 hover:bg-amber-100/80 border-2 border-amber-300 rounded-xl transition text-left group">
                      <div className="pr-2">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-amber-950">
                            Start for Missing Date ({formatDateFriendly(dateGapInfo.missingDates[0])})
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-amber-200 text-amber-900 rounded-full">
                            Missing
                          </span>
                        </div>
                        <p className="text-xs text-amber-700 mt-0.5">
                          Start operations for {formatDateFriendly(dateGapInfo.missingDates[0])} to maintain continuous sequence
                        </p>
                      </div>
                      <div className="bg-amber-500 group-hover:bg-amber-600 text-white p-2 rounded-lg ml-2 flex-shrink-0">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                    </button>
                  ) : (
                    <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-3 text-left">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-amber-950 uppercase tracking-wide">
                          Missing Dates ({dateGapInfo.missingDates.length} days missed)
                        </span>
                        <span className="text-[11px] text-amber-700">Click any date to start</span>
                      </div>
                      <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1">
                        {dateGapInfo.missingDates.map((mDate) => (
                          <button
                            key={mDate}
                            type="button"
                            onClick={() => handleStartDay(mDate)}
                            className="w-full flex items-center justify-between p-2.5 bg-white hover:bg-amber-100 border border-amber-300 rounded-lg text-left transition group">
                            <span className="font-semibold text-xs text-gray-900">
                              {formatDateFriendly(mDate)}
                            </span>
                            <span className="text-xs font-bold text-amber-700 group-hover:text-amber-900 flex items-center gap-1">
                              Start Day &rarr;
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* No date gap: Single clear button for today */
                <div className="mb-5">
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">Business Date to Start:</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatDateFriendly(todayKolkataStr)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Starting the day enables billing, counter orders, and stock updates for this date.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleStartDay(todayKolkataStr)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-bold text-base shadow-md transition flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Start Day for Today ({formatDateFriendly(todayKolkataStr)})
                  </button>
                </div>
              )}

              {/* Option to toggle custom date input if needed */}
              <div className="mt-2 text-center">
                <button
                  type="button"
                  onClick={() => setShowCustomDateInput(!showCustomDateInput)}
                  className="text-xs text-gray-500 hover:text-primaryColor hover:underline">
                  {showCustomDateInput ? "▲ Hide custom date" : "▼ Need a custom date?"}
                </button>
              </div>

              {showCustomDateInput && (
                <div className="mt-3 p-3 bg-gray-50 border rounded-xl text-left">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Select Custom Business Date:
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="border px-3 py-2 rounded-lg text-sm flex-1 focus:ring-1 focus:ring-primaryColor"
                    />
                    <button
                      type="button"
                      onClick={() => handleStartDay(selectedDate)}
                      disabled={!selectedDate}
                      className="bg-primaryColor hover:bg-primaryColor/90 text-white px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-50">
                      Start
                    </button>
                  </div>
                </div>
              )}

              {/* Cancel Button */}
              <div className="flex justify-center pt-4 border-t border-gray-100 mt-4">
                <button
                  type="button"
                  onClick={handleCancelDayStart}
                  className="text-gray-500 hover:text-gray-700 text-xs font-medium py-1 px-4 rounded hover:bg-gray-100 transition">
                  Cancel / Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {userData?.userType !== "admin" && <Header />}
      <div className="flex">
        <SideBar />
        <main className="flex-1 lg:ml-16 min-h-[93vh]">
          <Outlet />
        </main>
      </div>
    </>
  );
};

export default Dashboard;
